import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { themes } from '../../themes/colors';
import { getBlogStyles } from '../../themes/styles/blog';
import { getCommonStyles } from '../../themes/styles/common';
import Navigation from '../CookAI/Navigaton/Navigation';
import Footer from '../CookAI/Footer/Footer';
import { useLanguage } from '../../i18n/LanguageContext';
import { getBlogs, getBlogTags } from '../../services/blogApi';
import { BlogPost } from '../../types/blog';

const theme = themes['blue-tech'];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const BlogListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const s = getBlogStyles(theme);
  const commonStyles = getCommonStyles(theme);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [activeTag, setActiveTag] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 768) setIsMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getBlogs(language, page, 9, activeTag);
      setBlogs(res.data ?? []);
      setTotalPages(res.meta?.total_pages ?? 1);
    } catch {
      setError('Failed to load posts.');
    } finally {
      setLoading(false);
    }
  }, [language, page, activeTag]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  useEffect(() => {
    getBlogTags(language)
      .then((res) => setTags(res.data ?? []))
      .catch(() => {});
  }, [language]);

  // Reset to page 1 when language or tag changes
  useEffect(() => {
    setPage(1);
  }, [language, activeTag]);

  const handleNavigate = (section: string) => {
    navigate('/');
    setTimeout(() => {
      document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    setIsMenuOpen(false);
  };

  return (
    <div style={commonStyles.container}>
      <Navigation
        theme={theme}
        activeSection="blog"
        isMenuOpen={isMenuOpen}
        isMobile={isMobile}
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
        onNavigate={handleNavigate}
      />

      <div style={s.pageWrapper}>
        <div style={s.container}>
          {/* Header */}
          <div style={s.pageHeader}>
            <h1 style={s.pageTitle}>{t.nav.blog}</h1>
            <p style={s.pageSubtitle}>{t.blog.subtitle}</p>
          </div>

          {/* Tag filter */}
          {tags.length > 0 && (
            <div style={s.tagBar}>
              <button
                style={{
                  ...s.tagBtn,
                  ...(activeTag === undefined ? s.tagBtnActive : {}),
                }}
                onClick={() => setActiveTag(undefined)}
              >
                {t.blog.allTags}
              </button>
              {tags.map((tag) => (
                <button
                  key={tag}
                  style={{
                    ...s.tagBtn,
                    ...(activeTag === tag ? s.tagBtnActive : {}),
                  }}
                  onClick={() => setActiveTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* States */}
          {loading && <div style={s.loading}>Loading...</div>}

          {!loading && error && (
            <div style={s.errorBox}>
              <div style={s.emptyIcon}>⚠️</div>
              <p style={s.emptyText}>{error}</p>
            </div>
          )}

          {!loading && !error && blogs.length === 0 && (
            <div style={s.emptyState}>
              <div style={s.emptyIcon}>📝</div>
              <p style={s.emptyText}>{t.blog.noPosts}</p>
            </div>
          )}

          {/* Blog grid */}
          {!loading && !error && blogs.length > 0 && (
            <>
              <div style={s.grid}>
                {blogs.map((post) => (
                  <article
                    key={post.id}
                    style={{
                      ...s.card,
                      transform: hoveredCard === post.id ? 'translateY(-4px)' : 'none',
                      boxShadow:
                        hoveredCard === post.id
                          ? '0 12px 32px rgba(0,0,0,0.12)'
                          : '0 2px 8px rgba(0,0,0,0.05)',
                    }}
                    onMouseEnter={() => setHoveredCard(post.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => navigate(`/blog/${post.slug}`)}
                  >
                    {post.cover_image_url && (
                      <img
                        src={post.cover_image_url}
                        alt={post.title}
                        style={s.cardCover}
                        loading="lazy"
                      />
                    )}
                    <div style={s.cardBody}>
                      {post.tags?.length > 0 && (
                        <div style={s.cardTags}>
                          {post.tags.slice(0, 3).map((tag) => (
                            <span key={tag} style={s.cardTag}>{tag}</span>
                          ))}
                        </div>
                      )}
                      <h2 style={s.cardTitle}>{post.title}</h2>
                      <p style={s.cardSummary}>{post.summary}</p>
                      <div style={s.cardFooter}>
                        <span style={s.cardMeta}>
                          {post.read_time_minutes} {t.blog.minRead}
                          {post.published_at && ` · ${formatDate(post.published_at)}`}
                        </span>
                        <button style={s.readMoreBtn}>{t.blog.readMore}</button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={s.pagination}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      style={{
                        ...s.pageBtn,
                        ...(p === page ? s.pageBtnActive : {}),
                      }}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer
        theme={theme}
        onNavigateToLegal={(page) => navigate(`/${page}`)}
      />
    </div>
  );
};

export default BlogListPage;
